import i18nFactory from '@yandex-int/i18n';
import { uniqBy } from 'lodash';
import { Epic } from 'redux-observable';
import { EMPTY, Observable } from 'rxjs';

import { Action } from '../../actions';
import { showCommonDataFetchError } from '../../actions/common';
import {
    FETCH_RECOMMENDATIONS,
    fetchRecommendations,
    GET_RECOMMENDATION_APPLY_CARD_STATUS,
    getRecommendationApplyCardStatus,
    GetRecommendationCardStatus,
    SUBMIT_RECOMMENDATION_CARD,
    SubmitRecommendationCard,
    updateRecommendationCardStatus,
    updateRecommendations
} from '../../actions/recommendations';
import {
    isRecommendationSupported,
    mapRecommendationKeyFromApiFormat,
    mapSummaryRecommendationFromApiFormat
} from '../../mappers/recommendation';
import { confirm } from '../../service/Confirm/Confirm';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { showRequestResultNoticeMessage } from '../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import {
    getRecommendationsWithRefreshErrorStatusMessage,
    getSummaryRecommendationApplyStatusMessage,
    getSummaryRecommendationConfirmMessage
} from '../../service/RecommendationsHelpers/RecommendationsHelpers';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { GdRecommendationStatus, RecommendationsStatusesQuery, RecommendationStatus } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { RecommendationType } from '../../typings/recommendations';
import { State } from '../../typings/state';

import * as keyset from './recommendations.i18n';

const i18n = i18nFactory(keyset);

const fetchRecommendationsScreenDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(FETCH_RECOMMENDATIONS).mergeMap(
        (): Observable<Action> => {
            const ulogin = store$.value.commonData.login;

            return Observable.fromPromise(deps.recommendationsDataProvider.fetchRecommendationsScreenData(ulogin))
                .map(result =>
                    updateRecommendations(
                        result.data
                            .filter(recommendation => isRecommendationSupported(recommendation.type))
                            .map(mapSummaryRecommendationFromApiFormat),
                        result.reqId
                    )
                )
                .catch(
                    (): Observable<Action> => {
                        return Observable.of(showCommonDataFetchError(i18n('error-refresh')));
                    }
                );
        }
    );
};

const getRecommendationApplyCardStatusEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(GET_RECOMMENDATION_APPLY_CARD_STATUS).mergeMap((action: GetRecommendationCardStatus) => {
        const GET_STATUS_INTERVAL = 2000;
        const ulogin = store$.value.commonData.login;

        return Observable.timer(0, GET_STATUS_INTERVAL)
            .switchMap(() =>
                Observable.fromPromise(
                    deps.recommendationsDataProvider.getRecommendationsStatus(ulogin, action.enabledForApplyKeys)
                )
            )
            .first(
                (data: RecommendationsStatusesQuery) =>
                    data.client.recommendationsStatuses.length === action.enabledForApplyKeys.length
            )
            .map((data: RecommendationsStatusesQuery) => {
                let appliedItemsCount, requestedItemsCount;
                const { recommendationType, requestedKeys } = action;

                if (recommendationType === RecommendationType.removePagesFromBlackList) {
                    appliedItemsCount = uniqBy(
                        data.client.recommendationsStatuses.filter(
                            item => item && item.status === RecommendationStatus.DONE
                        ),
                        'cid'
                    ).length;
                    requestedItemsCount = uniqBy(requestedKeys, 'cid').length;
                } else {
                    appliedItemsCount = data.client.recommendationsStatuses.filter(
                        item => item && item.status === RecommendationStatus.DONE
                    ).length;
                    requestedItemsCount = requestedKeys.length;
                }

                const resultMessage = getSummaryRecommendationApplyStatusMessage(
                    appliedItemsCount,
                    requestedItemsCount,
                    recommendationType
                );

                showRequestResultNoticeMessage(requestedItemsCount, appliedItemsCount, resultMessage);

                return fetchRecommendations();
            });
    });
};

const submitRecommendationCardEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$
        .ofType(SUBMIT_RECOMMENDATION_CARD)
        .mergeMap(({ recommendationType, keys }: SubmitRecommendationCard) => {
            let itemsLength: number;

            if (recommendationType === RecommendationType.removePagesFromBlackList) {
                itemsLength = uniqBy(keys, 'cid').length;
            } else {
                itemsLength = keys.length;
            }

            const confirmMessage = getSummaryRecommendationConfirmMessage(recommendationType, itemsLength);

            return Observable.fromPromise(confirm({ message: confirmMessage }))
                .mergeMap(
                    (): Observable<Action> => {
                        const setProgressStatus$ = Observable.of(
                            updateRecommendationCardStatus(recommendationType, GdRecommendationStatus.IN_PROGRESS)
                        );

                        const makeRequest$ = Observable.fromPromise(
                            deps.recommendationsMutationExecutor.executeRecommendation(keys)
                        )
                            .map(data =>
                                getRecommendationApplyCardStatus(
                                    recommendationType,
                                    keys,
                                    data.executeRecommendation.items.map(mapRecommendationKeyFromApiFormat)
                                )
                            )
                            .catch(() => {
                                showNoticeMessage(
                                    NoticeType.error,
                                    getRecommendationsWithRefreshErrorStatusMessage(recommendationType)
                                );

                                return Observable.of(
                                    updateRecommendationCardStatus(recommendationType, GdRecommendationStatus.READY)
                                );
                            });

                        return Observable.concat(setProgressStatus$, makeRequest$);
                    }
                )
                .catch(
                    (): Observable<Action> => {
                        return EMPTY;
                    }
                );
        });
};

export { fetchRecommendationsScreenDataEpic, submitRecommendationCardEpic, getRecommendationApplyCardStatusEpic };
